import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AsyncTaskInfoDto, FisReportService, MyUserAccountService } from '@swagger/humanresources';
import { toBackendFailureReason } from '@shared/utils';
import {
  FIS_REPORT_TASK_TYPE,
  FisReportRequest,
  MAX_LISTED_REPORT_TASKS,
  ReportFile,
  ReportTask,
} from '@features/users/util-reports';
import { toFisReportRequestDto } from './fis-report-request.mapper';
import { fileNameFromDisposition } from './report-download-error.helper';

const CREATED = 'created';
const DOWNLOAD_FALLBACK_EXTENSION = '.xlsx';

/** The backend accepted the call but rejected the request (`error` in a 200 body). */
export class ReportRequestRejectedError extends Error {
  constructor(readonly reason: string | undefined) {
    super(reason ?? 'Report request rejected');
  }
}

/** Server calls of the FIS reports (backend #5211): start, list and download runs. */
@Injectable({ providedIn: 'root' })
export class FisReportClient {
  #fisReports = inject(FisReportService);
  #myAccount = inject(MyUserAccountService);

  /** Starts a run; resolves with the created background task. */
  async startReport(request: FisReportRequest): Promise<ReportTask> {
    const response = await firstValueFrom(
      this.#fisReports.fisReportStart({ body: toFisReportRequestDto(request) }),
    );
    const task = response.result;
    if (response.error || !task || !hasUId(task)) {
      throw new ReportRequestRejectedError(toBackendFailureReason(response));
    }
    return task;
  }

  /**
   * The user's report runs, newest first, in one request. The `type` filter
   * key is unconfirmed (#5248), so the result is filtered by type here too.
   */
  async listReportTasks(): Promise<ReportTask[]> {
    const response = await firstValueFrom(
      this.#myAccount.myUserAccountQueryAsyncTasks({
        body: {
          filter: { type: FIS_REPORT_TASK_TYPE },
          orderBy: [{ by: CREATED, desc: true }],
          take: MAX_LISTED_REPORT_TASKS,
        },
      }),
    );
    return (response.result ?? []).filter(isFisReportTask).filter(hasUId);
  }

  /** The finished file of a run; the name comes from the response header. */
  async downloadReport(asyncTaskUId: string): Promise<ReportFile> {
    const response = await firstValueFrom(
      this.#fisReports.fisReportDownloadFile$Response({ asyncTaskUId }),
    );
    return {
      blob: response.body,
      fileName: fileNameFromDisposition(
        response.headers,
        `${asyncTaskUId}${DOWNLOAD_FALLBACK_EXTENSION}`,
      ),
    };
  }
}

function isFisReportTask(task: AsyncTaskInfoDto): boolean {
  return task.type === FIS_REPORT_TASK_TYPE;
}

function hasUId(task: AsyncTaskInfoDto): task is ReportTask {
  return !!task.uId;
}
